import React, { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { Panel } from '@shared/components/bootstrap';
import { Pagination } from '@shared/components/pagination/pagination';
import { IPagination } from '@shared/types/pagination';

import { PhoneCallTable } from '@admin/components/phone_calls/table';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';

type Agent = {
  id: number;
  name: string;
};

type PhoneCallsFilters = {
  user_id?: number;
  page?: number;
};

type PhoneCall = React.ComponentProps<typeof PhoneCallTable>['calls'] extends (infer T)[] | undefined ? T : never;

type RestPagination = {
  current_page: number;
  next_page?: number;
  prev_page?: number;
  total_pages: number;
  total_count: number;
};

type PaginatedPhoneCalls = {
  results: PhoneCall[];
  pagination: RestPagination;
};

const LOAD_ERROR_MESSAGE = 'Unable to load phone calls. Please try again.';
const AGENTS_LOAD_ERROR_MESSAGE = 'Unable to load agents. Please try again.';

const toSharedPagination = (pagination: RestPagination): IPagination => ({
  __typename: 'Pagination',
  currentPage: pagination.current_page,
  nextPage: pagination.next_page ?? null,
  prevPage: pagination.prev_page ?? null,
  totalPages: pagination.total_pages,
  totalCount: pagination.total_count,
});

const parseFiltersFromSearch = (search: string): PhoneCallsFilters => {
  const params = new URLSearchParams(search);
  const filters: PhoneCallsFilters = {};

  const userID = params.get('user_id');
  if (userID && /^\d+$/.test(userID)) {
    const parsed = Number.parseInt(userID, 10);
    if (!Number.isNaN(parsed)) {
      filters.user_id = parsed;
    }
  }

  const page = params.get('page');
  if (page && /^\d+$/.test(page)) {
    const parsed = Number.parseInt(page, 10);
    if (!Number.isNaN(parsed)) {
      filters.page = parsed;
    }
  }

  return filters;
};

const syncFiltersToUrl = (filters: PhoneCallsFilters) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams();

  if (filters.user_id !== undefined) {
    searchParams.set('user_id', String(filters.user_id));
  }
  if (filters.page !== undefined) {
    searchParams.set('page', String(filters.page));
  }

  url.search = searchParams.toString();
  window.history.replaceState({}, '', url.toString());
};

export const PhoneCallsIndex: React.FC = () => {
  useTitle('Phone Calls');
  const [filters, setFilters] = useState<PhoneCallsFilters>(() => parseFiltersFromSearch(window.location.search));
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentsError, setAgentsError] = useState<string | undefined>();
  const [phoneCalls, setPhoneCalls] = useState<PhoneCall[] | undefined>();
  const [pagination, setPagination] = useState<RestPagination | undefined>();
  const [phoneCallsError, setPhoneCallsError] = useState<string | undefined>();
  const requestIdRef = useRef(0);
  const agentsRequestIdRef = useRef(0);

  const loadPhoneCalls = useCallback(async (activeFilters: PhoneCallsFilters) => {
    const requestId = ++requestIdRef.current;
    setPhoneCalls(undefined);
    setPagination(undefined);
    setPhoneCallsError(undefined);
    syncFiltersToUrl(activeFilters);

    try {
      const { data } = await axios.get<PaginatedPhoneCalls>('/phone_calls.json', {
        params: activeFilters,
      });

      if (requestId === requestIdRef.current) {
        setPhoneCalls(data.results);
        setPagination(data.pagination);
      }
    } catch {
      if (requestId === requestIdRef.current) {
        setPhoneCalls(undefined);
        setPagination(undefined);
        setPhoneCallsError(LOAD_ERROR_MESSAGE);
      }
    }
  }, []);

  const loadAgents = useCallback(async () => {
    const requestId = ++agentsRequestIdRef.current;
    setAgentsError(undefined);

    try {
      const { data } = await axios.get<Agent[]>('/phone_calls/callers.json');
      if (requestId === agentsRequestIdRef.current) {
        setAgents(data);
      }
    } catch {
      if (requestId === agentsRequestIdRef.current) {
        setAgents([]);
        setAgentsError(AGENTS_LOAD_ERROR_MESSAGE);
      }
    }
  }, []);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    void loadPhoneCalls(filters);
  }, [filters, loadPhoneCalls]);

  const onAgentChange = (userID?: number) => {
    setFilters({ user_id: userID, page: undefined });
  };

  const onPage = (page: number) => {
    setFilters((current) => ({ ...current, page }));
  };

  return (
    <div className="row">
      <div className="col-sm-12">
        <Panel>
          <Panel.Body>
            {agentsError && (
              <div className="alert alert-danger" role="alert">
                {agentsError}
              </div>
            )}
            <div className="row">
              <div className="col-sm-8">
                <label htmlFor="phone-calls-agent-filter">Filter by:</label>
                <select
                  id="phone-calls-agent-filter"
                  value={filters.user_id ?? ''}
                  onChange={(event) => onAgentChange(event.target.value ? Number(event.target.value) : undefined)}
                  disabled={!!agentsError}
                >
                  <option value="">All Agents</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {phoneCallsError && (
              <div className="alert alert-danger" role="alert">
                {phoneCallsError}
              </div>
            )}
            <div className="table-responsive">
              <PhoneCallTable calls={phoneCalls} />
            </div>
            {!phoneCalls && !phoneCallsError && <Spinner />}
            {pagination && !phoneCallsError && (
              <Pagination pagination={toSharedPagination(pagination)} onPage={onPage} />
            )}
          </Panel.Body>
        </Panel>
      </div>
    </div>
  );
};
